import { claimflowApiClient } from './claimflowApiClient';
import { toWorkflowExecutionError } from './taskService';
import type { WorkflowExecutionData } from './taskService';

/**
 * Estimation save — « Estimation règlement » (`EstimationCard`) and
 * « Estimation recours » (`EstimationRecoursCard`), ITEM-20261103-2139-xido.
 *
 * Each card saves with ONE call to a back-end workflow
 * (`POST /claimflow/workflows/<slug>/execute` with `{ context_data }`), which
 * runs the whole chain server-side, in one execution:
 *
 * | step                 | règlement `claimflow-save-estimation`            | recours `claimflow-save-estimation-recours` |
 * |----------------------|--------------------------------------------------|---------------------------------------------|
 * | `derive`             | input validation, `<CD_PSD>` map, total (server) | same                                        |
 * | `check-estimation`   | `WS2_GS_VER_ESTIMATION` (`estimation=reglement`) | same (`estimation=recours`)                 |
 * | `stop-if-ko`         | halts unless `VERIF` is exactly `"OK"`           | same                                        |
 * | writes               | `get-claim` → PUT perte totale ON / OFF → `write-provision` per `ecrire` poste | `write-prevision` per `ecrire` poste |
 * | `post-comment`       | best-effort, skipped without a comment           | same                                        |
 *
 * So the VEOS check can no longer be bypassed from the browser, what is
 * checked is what is written, and the total is computed server-side (the card
 * never sends one). The definitions seal their input: unknown keys are
 * dropped. Origami source of truth: `ClaimFlowSeeder::seedSaveEstimation*`
 * and `SaveEstimation{,Recours}WorkflowTest`.
 *
 * Responses, mapped to an {@link EstimationSaveResult} (this service never
 * throws):
 * - 201, `status: "completed"` ⇒ saved. `context_data["post-comment"].erp_error`
 *   ⇒ saved, but the comment was not sent (`commentError`).
 * - 422 failing at `stop-if-ko` (`error_code: WORKFLOW_HALTED`) ⇒ VEOS KO,
 *   nothing written: VEOS' `COMMENT` (the backend `message`) under the
 *   « Contrôle VEOS » title.
 * - 422 failing at `check-estimation` ⇒ the check could not be performed,
 *   nothing written (« Contrôle VEOS » title).
 * - 422 failing at `derive` (`INPUT_VALIDATION_FAILED`) ⇒ input error, nothing
 *   sent to VEOS.
 * - 422 failing at any later step, or a transport error ⇒ save error. Earlier
 *   writes of the run may have persisted (no rollback across VEOS calls); a
 *   resubmission converges, as the writes SET the amounts.
 */

/** Which card is saved — the `estimation` value of the VEOS check. */
export type EstimationKind = 'reglement' | 'recours';

/** The workflow slug of each card. */
export const ESTIMATION_SAVE_WORKFLOWS: Readonly<Record<EstimationKind, string>> = {
  reglement: 'claimflow-save-estimation',
  recours: 'claimflow-save-estimation-recours',
};

/** `POST` URL of the workflow that saves the `kind` card. */
export function estimationSaveUrl(kind: EstimationKind): string {
  return `/claimflow/workflows/${ESTIMATION_SAVE_WORKFLOWS[kind]}/execute`;
}

/** One poste of the estimation (`context_data.postes[]`). */
export interface EstimationPoste {
  /** Poste code (`CD_PSD`) — also its `<CD_PSD>=<amount>` key of the VEOS check. */
  code: string;
  /**
   * The amount as saved, a JSON number, SIGNED: deduction postes negative
   * (`savedPosteAmount`, `components/deductionPostes.ts`).
   */
  mnt: number;
  /**
   * `true`: written by this save (`sinistre-provision` / `sinistre-prevision`).
   * Every poste sent is checked and totalled, written or not.
   */
  ecrire: boolean;
}

interface EstimationSaveCommon {
  /** Claim id → `id_sin` (sent as an integer). */
  idSin: string | number;
  /** Selected garantie `CD_GAR` → `garantie` (check + `cdGar` of every write). */
  garantie: string;
  /** Every poste to check (at most 100 — the workflow rejects more). */
  postes: readonly EstimationPoste[];
  /** The trimmed comment → `commentaire` (`''` when none: no comment posted). */
  commentaire: string;
  /** The comment author `CD_UTIL` → `creator` (required by the workflow with a comment). */
  creator: string | null;
}

/** « Estimation règlement » (`claimflow-save-estimation`). */
export interface EstimationReglementSave extends EstimationSaveCommon {
  estimation: 'reglement';
  /** → `perte_totale`: ON sets `infos.PERTETOTALE = "1"` + `MTVRADE`, OFF `"0"` + null. */
  perteTotale: boolean;
  /** → `vrade`: the VRADE amount, only read when `perteTotale` is on. */
  vrade: number | null;
}

/** « Estimation recours » (`claimflow-save-estimation-recours`) — no perte totale. */
export interface EstimationRecoursSave extends EstimationSaveCommon {
  estimation: 'recours';
}

export type EstimationSaveParams = EstimationReglementSave | EstimationRecoursSave;

/** Why a save did not complete (see the file header). */
export type EstimationSaveFailure =
  | 'veos-ko'
  | 'check-unavailable'
  | 'invalid-input'
  | 'save-failed';

/**
 * `ok: true` = saved; `commentError` is the reason the comment was not sent
 * (`null` when it was sent or there was none). `ok: false` = not saved (or, for
 * `save-failed`, possibly partly): the card shows `title` (set only for the two
 * VEOS-check failures, « Contrôle VEOS ») and `message` in its error `Alert`.
 */
export type EstimationSaveResult =
  | { ok: true; commentError: string | null }
  | { ok: false; failure: EstimationSaveFailure; title?: string; message: string };

/** User-facing messages of the save (exported for the card tests). */
export const ESTIMATION_SAVE_TEXT = {
  /**
   * Title of the card alert of a blocked VEOS check: VEOS' bare `COMMENT`
   * reads like an echo of the user's own comment without it.
   */
  veosAlertTitle: 'Contrôle VEOS',
  /** A KO without any message (the workflow normally always sends one). */
  koFallback: 'Estimation refusée par le contrôle VEOS.',
  /** The `check-estimation` call itself failed: nothing was written. */
  checkUnavailable: (detail: string) =>
    `Le contrôle VEOS de l’estimation n’a pas pu être effectué (${detail}). Aucune modification n’a été enregistrée.`,
  technicalFallbackDetail: 'erreur technique',
  invalidClaim: 'Identifiant de sinistre invalide.',
  /** A save error without any message, per card. */
  saveFailed: {
    reglement: 'Échec de l’enregistrement de la provision.',
    recours: 'Échec de l’enregistrement de l’estimation.',
  },
} as const;

/** Step names of the seeded workflows the failure mapping reads. */
const STEP_DERIVE = 'derive';
const STEP_CHECK = 'check-estimation';
const STEP_STOP_IF_KO = 'stop-if-ko';
const STEP_POST_COMMENT = 'post-comment';

const ERROR_CODE_HALTED = 'WORKFLOW_HALTED';
const ERROR_CODE_INVALID_INPUT = 'INPUT_VALIDATION_FAILED';

function asRecord(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' ? (value as Record<string, unknown>) : null;
}

function nonEmpty(value: unknown): string | null {
  return typeof value === 'string' && value.trim() !== '' ? value : null;
}

/** The claim id as the integer the workflow's `id_sin` input expects. */
function toClaimId(idSin: string | number): number | null {
  if (typeof idSin === 'string' && idSin.trim() === '') return null;
  const id = Number(idSin);
  return Number.isInteger(id) && id > 0 ? id : null;
}

/** The `context_data` of the workflow — the contract keys only. */
export function buildEstimationSaveContext(
  params: EstimationSaveParams,
  idSin: number
): Record<string, unknown> {
  const context: Record<string, unknown> = {
    id_sin: idSin,
    garantie: params.garantie,
    postes: params.postes.map(({ code, mnt, ecrire }) => ({ code, mnt, ecrire })),
  };
  if (params.estimation === 'reglement') {
    context.perte_totale = params.perteTotale;
    context.vrade = params.vrade;
  }
  context.commentaire = params.commentaire;
  context.creator = params.creator;
  return context;
}

/**
 * The detail of a failed request with no workflow trail: the ERP proxy
 * `error.message`, the body `message`, then the JS error — `null` when none.
 */
function transportDetail(error: unknown): string | null {
  const data = asRecord(asRecord(asRecord(error)?.response)?.data);
  return (
    nonEmpty(asRecord(data?.error)?.message) ??
    nonEmpty(data?.message) ??
    (error instanceof Error ? nonEmpty(error.message) : null)
  );
}

/**
 * Why the comment step did not send the comment: `post-comment.erp_error`
 * (`continue_on_error` — the save itself completed). `null` when it was sent
 * or skipped (no comment).
 */
function commentErrorOf(contextData: Record<string, unknown> | undefined): string | null {
  const erpError = asRecord(contextData?.[STEP_POST_COMMENT])?.erp_error;
  if (erpError === null || erpError === undefined) return null;
  const error = asRecord(erpError);
  return (
    nonEmpty(erpError) ??
    nonEmpty(error?.message) ??
    nonEmpty(asRecord(error?.data)?.message) ??
    ESTIMATION_SAVE_TEXT.technicalFallbackDetail
  );
}

function failed(
  failure: EstimationSaveFailure,
  message: string,
  title?: string
): EstimationSaveResult {
  return title === undefined
    ? { ok: false, failure, message }
    : { ok: false, failure, title, message };
}

/** Map a rejected execute call to the card's error (see the file header). */
function failureOf(error: unknown, kind: EstimationKind): EstimationSaveResult {
  const fallback = ESTIMATION_SAVE_TEXT.saveFailed[kind];
  const workflowError = toWorkflowExecutionError(error);
  if (!workflowError) {
    return failed('save-failed', transportDetail(error) ?? fallback);
  }

  const failedStep = workflowError.stepExecutions.find((step) => step.status === 'failed');
  const stepName = failedStep?.name ?? null;
  // The backend `message` (= the failed step's error), else the step's own.
  const message =
    nonEmpty(asRecord(asRecord(asRecord(error)?.response)?.data)?.message) ??
    nonEmpty(failedStep?.errorMessage);

  if (
    stepName === STEP_STOP_IF_KO ||
    (stepName === null && workflowError.errorCode === ERROR_CODE_HALTED)
  ) {
    return failed(
      'veos-ko',
      message ?? ESTIMATION_SAVE_TEXT.koFallback,
      ESTIMATION_SAVE_TEXT.veosAlertTitle
    );
  }
  if (stepName === STEP_CHECK) {
    return failed(
      'check-unavailable',
      ESTIMATION_SAVE_TEXT.checkUnavailable(
        message ?? ESTIMATION_SAVE_TEXT.technicalFallbackDetail
      ),
      ESTIMATION_SAVE_TEXT.veosAlertTitle
    );
  }
  if (
    stepName === STEP_DERIVE ||
    (stepName === null && workflowError.errorCode === ERROR_CODE_INVALID_INPUT)
  ) {
    return failed('invalid-input', message ?? fallback);
  }
  return failed('save-failed', message ?? fallback);
}

export const estimationSaveService = {
  /**
   * Save the estimation through its workflow — one call. Never throws: every
   * outcome resolves to an {@link EstimationSaveResult}. A claim id that is not
   * a positive integer is rejected before any request.
   */
  async save(params: EstimationSaveParams): Promise<EstimationSaveResult> {
    const idSin = toClaimId(params.idSin);
    if (idSin === null) {
      return failed('invalid-input', ESTIMATION_SAVE_TEXT.invalidClaim);
    }

    let execution: WorkflowExecutionData | undefined;
    try {
      const response = await claimflowApiClient.post<{ data?: WorkflowExecutionData }>(
        estimationSaveUrl(params.estimation),
        { context_data: buildEstimationSaveContext(params, idSin) }
      );
      execution = response.data.data;
    } catch (error) {
      return failureOf(error, params.estimation);
    }

    if (execution?.status !== 'completed') {
      return failed(
        'save-failed',
        nonEmpty(execution?.error_message) ?? ESTIMATION_SAVE_TEXT.saveFailed[params.estimation]
      );
    }
    return { ok: true, commentError: commentErrorOf(execution.context_data) };
  },
};
